/**
 * Contract → conversations block views. Pure; unit-tested against the
 * prototype's own math (`ConversationsPanel`, `BreakdownBody`).
 */
import type { BreakdownView, ChartSeriesToken, ConversationRowView } from '@deepdive-ui';

import type {
  CommunityDimension,
  ConversationRow,
  DrillRef,
  FilterOptions,
  PostBreakdown,
} from '@/data-sources/contract';
import {
  BREAKDOWN_SENTIMENTS,
  breakdownSummary,
  sentimentBase,
  postedLabelCompact,
  totalReactionsOf,
} from '@/lib/analytics/conversations';
import { SENTIMENT_TOKENS, labelOf } from '@/lib/analytics/series';

const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const pct = (value: number) => `${value.toFixed(1)}%`;

/** What page code asks to open. Labels ride along only where the options cannot supply them. */
export type OpenConversationsRequest =
  | { kind: 'point'; drill: DrillRef }
  | { kind: 'page'; brand: string; channel: string }
  | {
      kind: 'community';
      dimension: CommunityDimension;
      value: string;
      /** The name the table showed ("@gpcarehelp", "GP Users BD"). */
      label: string;
    };

export type ConversationsHeading = {
  title: string;
  /** Who or what the list is for — goes in the badge. */
  subject: string;
  label: string;
  helpText: string;
  helpSupportingText: string;
};

const COMMUNITY_TITLES: Readonly<Record<CommunityDimension, string>> = {
  voice: 'Top voices conversations',
  community: 'Top communities by mention conversations',
};

/** The modal's title and copy, per what opened it — the prototype's three modals' strings. */
export function presentConversationsHeading(
  request: OpenConversationsRequest,
  options: FilterOptions,
): ConversationsHeading {
  switch (request.kind) {
    case 'point': {
      const subject = request.drill.subject;
      return {
        title: request.drill.chart,
        subject,
        label: `Conversations for ${subject}`,
        helpText: `Every post behind ${subject || 'this point'} on this chart, in the period and filters you have set.`,
        helpSupportingText:
          'The count matches the point you opened: narrow the chips or the date range and both move together.',
      };
    }
    case 'page': {
      const subject = `${labelOf(options, 'brand', request.brand)} on ${labelOf(options, 'channel', request.channel)}`;
      return {
        title: 'Page conversations',
        subject,
        label: `Conversations for ${subject}`,
        helpText:
          'Every post behind this page — one brand on one channel — in the period and filters you have set.',
        helpSupportingText:
          'The figures add up to the row you clicked: its Total posts, Total engagement, Reactions, Comments and Shares. Reactions include likes.',
      };
    }
    case 'community': {
      const subject = request.label;
      return {
        title: COMMUNITY_TITLES[request.dimension],
        subject,
        label: `Conversations for ${subject}`,
        helpText:
          request.dimension === 'voice'
            ? `Every conversation ${subject} posted, in the period and filters you have set.`
            : `Every conversation in ${subject}, in the period and filters you have set.`,
        helpSupportingText:
          'The count matches the row you clicked: its Conversations cell and this list are the same posts.',
      };
    }
  }
}

export function presentConversationRow(
  row: ConversationRow,
  options: FilterOptions,
): ConversationRowView {
  return {
    id: row.id,
    text: row.text,
    channel: row.channel,
    channelLabel: labelOf(options, 'channel', row.channel),
    sentiment: row.sentiment,
    sentimentLabel: labelOf(options, 'sentiment', row.sentiment),
    intentLabel: labelOf(options, 'intent', row.intent),
    topicLabel: labelOf(options, 'topic', row.topic),
    reactions: row.reactions,
    comments: row.comments,
    shares: row.shares,
    engagement: row.engagement,
    postedLabel: postedLabelCompact(Date.parse(row.postedAt)),
  };
}

/** `BreakdownBody`'s derived figures, from the contract breakdown. */
export function presentBreakdown(
  b: PostBreakdown,
  options: FilterOptions,
  noun: 'post' | 'conversation',
): BreakdownView {
  const sentimentLabel = (s: string) => labelOf(options, 'sentiment', s);
  const base = sentimentBase(b);
  const share = (s: (typeof BREAKDOWN_SENTIMENTS)[number]) =>
    base === 0 ? 0 : (b.commentSentiment[s] / base) * 100;
  const net = share('positive') - share('negative');
  const totalReactions = totalReactionsOf(b);
  const top = [...b.reactions].sort((x, y) => y.count - x.count)[0];
  const colors = BREAKDOWN_SENTIMENTS.map((s) => SENTIMENT_TOKENS[s] as ChartSeriesToken);

  return {
    totalComments: whole.format(b.totalComments),
    relevantComments: whole.format(b.relevantComments),
    totalShares: whole.format(b.totalShares),
    summary: breakdownSummary(b, sentimentLabel, noun),
    hasComments: b.relevantComments !== 0,
    sentimentRows: BREAKDOWN_SENTIMENTS.map((s) => ({
      x: sentimentLabel(s),
      comments: b.commentSentiment[s],
    })),
    sentimentColors: colors,
    sentimentLegend: BREAKDOWN_SENTIMENTS.map((s, i) => ({
      label: sentimentLabel(s),
      color: colors[i]!,
    })),
    /* No badges, on purpose: a `KPI` badge is green or red, and a neutral fact would read as news. */
    sentimentKpis: [
      { label: 'Net sentiment', value: `${net > 0 ? '+' : ''}${net.toFixed(1)}` },
      { label: 'Negative share', value: pct(share('negative')) },
    ],
    positiveShare: pct(share('positive')),
    hasReactions: totalReactions !== 0,
    reactionRows: b.reactions.map((r) => ({ x: r.label, count: r.count })),
    reactionKpis: [
      { label: 'Total reactions', value: whole.format(totalReactions) },
      { label: 'Top reaction', value: top && totalReactions > 0 ? top.label : '—' },
    ],
    topComments: b.topComments.map((c) => ({
      id: c.id,
      text: c.text,
      reactions: whole.format(c.reactions),
      sentiment: c.sentiment,
      sentimentLabel: sentimentLabel(c.sentiment),
    })),
  };
}
